import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

type Props = {
  children: ReactNode;
  className?: string;
  /** sm = section eyebrows; md = hero tagline */
  size?: "sm" | "md";
};

/** Skewed parallelogram label · layered accents match the AutoPlus logo. */
const SKEW = "-skew-x-[17deg]";
const UNSKEW = "skew-x-[17deg]";

export function BrandEyebrowBadge({ children, className, size = "sm" }: Props) {
  const padding =
    size === "md"
      ? "px-5 py-2 md:px-6 md:py-2.5"
      : "px-3.5 py-1.5 sm:px-4 sm:py-2";

  const text =
    size === "md"
      ? "text-sm font-bold md:text-base"
      : "text-xs font-semibold sm:text-sm";

  return (
    <span
      className={cn(
        "inline-flex overflow-visible",
        /* Room on the left for accent layers (matches logo layout) */
        "pl-5 pr-0.5",
        size === "md" ? "mb-5" : "mb-3",
        className,
      )}
    >
      <span className="relative inline-flex overflow-visible">
        {/* Pale accent · furthest back, peeks on the left */}
        <span
          aria-hidden
          className={cn(
            "pointer-events-none absolute inset-0 z-0",
            SKEW,
            "bg-(--logo-blue-light)",
            "-translate-x-[14px]",
          )}
        />

        {/* Brand blue · middle layer */}
        <span
          aria-hidden
          className={cn(
            "pointer-events-none absolute inset-0 z-[1]",
            SKEW,
            "bg-(--logo-blue)",
            "-translate-x-[7px]",
          )}
        />

        {/* Black front with text */}
        <span
          className={cn(
            "relative z-[2] inline-flex max-w-full items-center",
            SKEW,
            "bg-black text-white",
            padding,
            "shadow-[0_4px_16px_rgba(0,0,0,0.22)]",
          )}
        >
          <span
            dir="rtl"
            className={cn(UNSKEW, "text-balance italic leading-snug", text)}
          >
            {children}
          </span>
        </span>
      </span>
    </span>
  );
}
